import { useEffect, useRef, type ChangeEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import InputBase from '@mui/material/InputBase';

/** 숫자만 붙여 'H:mm' / 'H:mm:ss' 그룹으로 자동 정렬한다. 두 자리를 채우는 즉시 ':' 를 붙여
 *  (카드 유효기간 입력처럼) 다음 그룹으로 넘어갈 차례임을 바로 보여준다. */
function formatDurationDigits(digits: string, negative: boolean, withSeconds: boolean) {
  let body = digits;
  if (withSeconds && digits.length >= 4) {
    body = `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 6)}`;
  } else if (digits.length >= 2) {
    body = `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }
  return (negative ? '-' : '') + body;
}

type DurationInputProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: ReactKeyboardEvent) => void;
  placeholder?: string;
  autoFocus?: boolean;
  sx?: Record<string, unknown>;
  /** 초 자리까지 입력받을지. false면 'H:mm' 두 그룹까지만 (기본 true) */
  withSeconds?: boolean;
  /** 맨 앞 '-' 입력을 허용할지 (기본 true) */
  allowNegative?: boolean;
}>;

/**
 * 시간 입력. ':' 를 직접 입력하지 않아도 숫자를 치면 자동으로 'H:mm' 또는 'H:mm:ss' 형태로 정렬된다.
 * 부호(조기 컷 등 음수 시간)가 필요 없는 곳은 allowNegative={false} 로 끈다.
 */
function DurationInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  autoFocus,
  sx,
  withSeconds = true,
  allowNegative = true,
}: DurationInputProps) {
  const prevValueRef = useRef(value);
  const maxDigits = withSeconds ? 6 : 4;

  useEffect(() => {
    prevValueRef.current = value;
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const negative = allowNegative && raw.includes('-');
    const prevDigits = prevValueRef.current.replace(/\D/g, '');
    let digits = raw.replace(/\D/g, '').slice(0, maxDigits);

    // 커서가 자동 삽입된 ':' 바로 뒤에 있을 때 Backspace를 눌러도 ':'만 지워지고
    // 숫자는 그대로 남아 재포맷 시 값이 그대로 복원되는(멈춰 보이는) 문제를 보정한다.
    const isDeletion = raw.length < prevValueRef.current.length;
    if (isDeletion && digits.length === prevDigits.length && digits.length > 0) {
      digits = digits.slice(0, -1);
    }

    onChange(formatDurationDigits(digits, negative, withSeconds));
  };

  return (
    <InputBase
      autoFocus={autoFocus}
      fullWidth
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onKeyDown={onKeyDown}
      sx={sx}
    />
  );
}

export default DurationInput;
